<template>
    <canvas
        ref="canvas"
        :height="canvasHeight"
        :style="canvasStyle"
        :width="canvasWidth"
        class="beam-canvas"
    />
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, watch, computed} from "vue";
import {strOperations} from "@/composables/globals";

const props = defineProps({
    mapRef: {required: true},
    isMapReady: {
        default: false
    },
    devices: {
        default: [],
        required: true
    },
    beamLength: {type: Number, default: 100}
});

const canvas = ref(null);
const canvasWidth = ref(0);
const canvasHeight = ref(0);

const canvasStyle = computed(() => ({
    position: "absolute",
    pointerEvents: "none",
    left: "0",
    top: "0",
    width: canvasWidth.value + "px",
    height: canvasHeight.value + "px",
    zIndex: 450
}));

function getMapContainer() {
    // Works with vue-leaflet and native Leaflet
    return props.mapRef?.leafletObject?.getContainer?.() || props.mapRef?.$el;
}

function syncCanvasToMap() {
    const mapEl = getMapContainer();
    if (!mapEl) return;
    const rect = mapEl.getBoundingClientRect();
    canvasWidth.value = rect.width;
    canvasHeight.value = rect.height;
    // For device pixel ratio support
    const dpr = window.devicePixelRatio || 1;
    if (canvas.value) {
        canvas.value.width = rect.width * dpr;
        canvas.value.height = rect.height * dpr;
        canvas.value.style.width = rect.width + "px";
        canvas.value.style.height = rect.height + "px";
    }
}

function getCompassLabel(heading) {
    const compass = heading % 360;
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    return directions[Math.round(compass / 45) % 8];
}

function drawBeams() {
    syncCanvasToMap();
    const ctx = canvas.value?.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    if (!ctx)
        return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvasWidth.value, canvasHeight.value);

    if (!props.devices.length)
        return;

    for (const device of props.devices) {
        // Coerce and CHECK, rather than trusting the device object. A lat that
        // is not a finite number makes Leaflet throw "Invalid LatLng object"
        // from inside a canvas redraw - including the one on the map's drag
        // handler, where it escapes as an uncaught error and stops the live map
        // updating at all. One bad device must cost its own beam, nothing more.
        const lat = Number(device.lat);
        const lng = Number(device.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const point = props.mapRef.leafletObject.latLngToContainerPoint([lat, lng]);
        const center = {x: point.x, y: point.y};
        const heading = Number(device.heading) || 0;
        const spread = Math.PI / 10; // Widen if needed
        const angleRad = (heading - 90) * Math.PI / 180;
        const beamLength = props.beamLength;

        const leftX = center.x + beamLength * Math.cos(angleRad - spread);
        const leftY = center.y + beamLength * Math.sin(angleRad - spread);
        const rightX = center.x + beamLength * Math.cos(angleRad + spread);
        const rightY = center.y + beamLength * Math.sin(angleRad + spread);
        const tipX = center.x + beamLength * Math.cos(angleRad);
        const tipY = center.y + beamLength * Math.sin(angleRad);

        // Beam gradient
        const grad = ctx.createLinearGradient(center.x, center.y, tipX, tipY);
        grad.addColorStop(0, 'rgba(255,0,0,0.9)');
        grad.addColorStop(1, 'rgba(255,0,255,0)');

        ctx.globalAlpha = 0.78;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(center.x, center.y);
        ctx.lineTo(leftX, leftY);
        ctx.lineTo(rightX, rightY);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Compass label
        ctx.font = "bold 16px sans-serif";
        ctx.textAlign = "center";
        ctx.strokeStyle = "darkblue";
        ctx.lineWidth = 3;
        ctx.strokeText(getCompassLabel(heading), tipX, tipY);
        ctx.fillStyle = "white";
        ctx.fillText(getCompassLabel(heading), tipX, tipY);

        // Speed label
        const midX = (center.x + tipX) / 2;
        const midY = (center.y + tipY) / 2;
        const regex = /^([-+]?\d*\.?\d+)(.*)$/;
        let speed = 0, unit = ''
        if (device.gps_speed) {
            // Prefer the server-resolved speed_mph: gps_speed carries MPH from
            // hardware and km/h x2 from phones, and only the server can tell
            // them apart. Formatted into a local, NOT written back onto the
            // device - the previous write-back mutated a shared object that
            // another component also stringifies.
            //
            // gps_speed_raw, never gps_speed: DevicesMap writes an already
            // formatted string ("72.41kph") into gps_speed, and re-formatting
            // that string multiplied a km user's reading by 1.609 a second time.
            const rawNumeric = typeof device.gps_speed === 'number'
                ? device.gps_speed
                : (device.gps_speed_raw ?? null);
            const formatted = rawNumeric === null && device.speed_mph == null
                ? String(device.gps_speed)
                : String(strOperations().unitifySpeed(device, rawNumeric));

            [, speed, unit] = formatted.match(regex) || [];
        }

        ctx.font = "bold 14px sans-serif";
        ctx.strokeStyle = "darkblue";
        ctx.lineWidth = 3;
        ctx.strokeText(`${parseInt(speed)} ${unit}`, midX, midY);
        ctx.fillStyle = "yellow";
        ctx.fillText(`${parseInt(speed)} ${unit}`, midX, midY);
    }
}

// Sync on mount, resize, map move/zoom, and data changes
onMounted(() => {
    syncCanvasToMap();
    // Attach directly atop the map container
    const mapEl = getMapContainer();
    if (mapEl && canvas.value && !mapEl.contains(canvas.value)) {
        mapEl.appendChild(canvas.value); // ensures absolute overlay
    }
    const resize = () => {
        syncCanvasToMap();
        drawBeams();
    };
    window.addEventListener("resize", resize);

    onBeforeUnmount(() => {
        const leaflet = props.mapRef?.leafletObject;
        window.removeEventListener("resize", resize);
        if (leaflet) leaflet.off("move zoom resize", drawBeams);
        // If needed, remove canvas from DOM
        if (mapEl && canvas.value && mapEl.contains(canvas.value)) {
            mapEl.removeChild(canvas.value);
        }
    });
});
watch(() => props.isMapReady, (newVal, oldVal) => {
    if (newVal) {
        const leaflet = props.mapRef?.leafletObject;
        if (leaflet) {
            leaflet.on("move zoom resize", drawBeams);
        }
    }
}, {immediate: true, deep: true});
watch(() => props.devices, (newVal, oldVal) => {
    drawBeams()
}, {immediate: true, deep: true});
</script>

<style scoped>
.beam-canvas {
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 450;
}
</style>
